/**
 * PriceTag — money with a superscript currency symbol, tabular numerals, and sale
 * strike-through.
 *
 * Ported 1:1 from `tw-components.jsx:499`, including its own `fmtPrice`. Deviations
 * are mechanical only: `cx` is imported, and there's no `import React`.
 *
 * **On CLAUDE.md rule 1 ("money → formatCurrency, never .toFixed(2)").** The rule's
 * purpose is to stop hand-rolled formatting that loses thousands separators.
 * `fmtPrice` is `toLocaleString` with the same `minimumFractionDigits: 2` /
 * `maximumFractionDigits: 2` the app's `formatPrice` uses, and `en-CA` and the app's
 * `en-US` render currency amounts identically (both `,` groups, `.` decimal), so
 * there is no visible divergence. It stays inline because `design-system/` may not
 * import `config/config` — that would break the extraction cut line.
 *
 * It does **not** guard null/NaN the way the app's `formatPrice` does, so callers
 * must pass numbers. `original` is safe to leave undefined: the `onSale` test short-
 * circuits before it reaches `fmtPrice`.
 */
import { cx } from "./cx";

const PRICE_SIZE = { sm: "text-lg", md: "text-2xl", lg: "text-4xl" };

function fmtPrice(n) {
  return Number(n).toLocaleString("en-CA", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

export function PriceTag({
  amount,
  original,
  currency = "$",
  unit,
  size = "md",
  style = {},
  className = "",
}) {
  const onSale = original !== undefined && original > amount;
  return (
    <span
      className={cx(
        "dnd-price inline-flex items-baseline gap-[var(--space-2)] font-sans",
        className
      )}
      style={style}
    >
      <span
        className={cx(
          "font-bold tracking-[var(--tracking-tight)] tabular-nums",
          PRICE_SIZE[size] || PRICE_SIZE.md,
          onSale ? "text-orange-600" : "text-ink"
        )}
      >
        <span className="text-[0.62em] font-semibold self-start align-top">
          {currency}
        </span>
        {fmtPrice(amount)}
      </span>
      {onSale && (
        <span className="text-ink-3 line-through text-[0.7em] font-medium">
          {currency}
          {fmtPrice(original)}
        </span>
      )}
      {unit && <span className="text-ink-2 text-sm font-medium">{unit}</span>}
    </span>
  );
}
